import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrash, 
  faCopy, 
  faToggleOn, 
  faToggleOff,
  faGripVertical 
} from '@fortawesome/free-solid-svg-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableRow = ({ 
  rule, 
  rulesetTypes, 
  getRuleTypeLabel, 
  getStatusBadge, 
  onEdit, 
  onClone, 
  onToggleActive, 
  onDelete 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? '#e3f2fd' : 'white',
    boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.2)' : 'none',
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td {...attributes} {...listeners} style={{ cursor: 'grab', textAlign: 'center' }}>
        <FontAwesomeIcon icon={faGripVertical} style={{ color: '#999' }} />
      </td>
      <td>
        <strong>{rule.name}</strong>
        {rule.description && (
          <div style={{ fontSize: '0.85em', color: '#666' }}>
            {rule.description}
          </div>
        )}
      </td>
      <td>{getRuleTypeLabel(rule.ruleType)}</td>
      <td>
        <span style={{ 
          display: 'inline-block',
          backgroundColor: '#007bff',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.9em',
          fontWeight: 'bold'
        }}>
          {rule.executionOrder}
        </span>
      </td>
      <td>{getStatusBadge(rule.isActive)}</td>
      <td>
        {rule.extractionRulesetTypeId 
          ? rulesetTypes.find(t => t.id === rule.extractionRulesetTypeId)?.name || 'N/A'
          : 'All'
        }
      </td>
      <td>
        <button
          onClick={() => onEdit(rule)}
          className="btn btn-outline-secondary btn-sm mx-1"
          title="Edit Rule"
        >
          <FontAwesomeIcon icon={faEdit} aria-hidden="true" />
          <span className="sr-only">Edit</span>
        </button>
        <button
          onClick={() => onClone(rule)}
          className="btn btn-outline-info btn-sm mx-1"
          title="Clone Rule"
        >
          <FontAwesomeIcon icon={faCopy} aria-hidden="true" />
          <span className="sr-only">Clone</span>
        </button>
        <button
          onClick={() => onToggleActive(rule)}
          className={`btn btn-outline-${rule.isActive ? 'warning' : 'success'} btn-sm mx-1`}
          title={rule.isActive ? 'Deactivate' : 'Activate'}
        >
          <FontAwesomeIcon icon={rule.isActive ? faToggleOff : faToggleOn} aria-hidden="true" />
          <span className="sr-only">{rule.isActive ? 'Deactivate' : 'Activate'}</span>
        </button>
        <button
          onClick={() => onDelete(rule)}
          className="btn btn-outline-danger btn-sm mx-1"
          title="Delete Rule"
        >
          <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
          <span className="sr-only">Delete</span>
        </button>
      </td>
    </tr>
  );
};

const RulesTable = ({ 
  rules, 
  rulesetTypes, 
  onEdit, 
  onClone, 
  onToggleActive, 
  onDelete, 
  onReorder 
}) => {
  const ruleTypes = [
    { value: '', label: 'All Rule Types' },
    { value: 'STRING_MANIPULATION', label: 'String Manipulation' },
    { value: 'CONDITIONAL', label: 'Conditional Logic' },
    { value: 'LOOKUP', label: 'Reference Data Lookup' }
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getRuleTypeLabel = (ruleType) => {
    const type = ruleTypes.find(t => t.value === ruleType);
    return type ? type.label : ruleType;
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return <span style={{ color: 'green', fontWeight: 'bold' }}>● Active</span>;
    }
    return <span style={{ color: 'gray', fontWeight: 'bold' }}>● Inactive</span>;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = rules.findIndex((rule) => rule.id === active.id);
    const newIndex = rules.findIndex((rule) => rule.id === over.id);

    const reorderedRules = arrayMove(rules, oldIndex, newIndex);
    onReorder(reorderedRules);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <table className="users-table">
        <thead>
          <tr>
            <th style={{ width: '40px' }}></th>
            <th>Name</th>
            <th>Type</th>
            <th>Execution Order</th>
            <th>Status</th>
            <th>Ruleset Type</th>
            <th>Actions</th>
          </tr>
        </thead>
        <SortableContext
          items={rules.map(rule => rule.id)}
          strategy={verticalListSortingStrategy}
        >
          <tbody>
            {rules.map((rule) => (
              <SortableRow
                key={rule.id}
                rule={rule}
                rulesetTypes={rulesetTypes}
                getRuleTypeLabel={getRuleTypeLabel}
                getStatusBadge={getStatusBadge}
                onEdit={onEdit}
                onClone={onClone}
                onToggleActive={onToggleActive}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </SortableContext>
      </table>
    </DndContext>
  );
};

export default RulesTable;
